<template>
    <div
        class="bo-list-box"
        :class="{ 'fit-width': isFitWidth }"
        :style="isFitWidth ? { maxWidth: fitWidth + 'px' } : null"
    >
        <div
            v-for="(label, index) in visibleLabels"
            :key="index"
            class="list-box-item"
            data-toggle="tooltip"
            data-placement="bottom"
            v-bind="labelTooltipAttributes(label)"
            v-on="tooltipListeners"
        >
            {{ label }}
        </div>
        <div
            v-if="collapsedLabels.length > 0"
            class="list-box-item list-box-more"
            data-toggle="tooltip"
            data-placement="bottom"
            v-bind="moreTooltipAttributes"
            v-on="tooltipListeners"
        >
            + {{ collapsedLabels.length }}
        </div>

        <!-- fitWidth: every tag laid out unseen, to measure how many fit -->
        <div
            v-if="isFitWidth"
            ref="measure"
            class="list-box-measure"
            aria-hidden="true"
        >
            <div
                v-for="(label, index) in labels"
                :key="index"
                class="list-box-item"
            >
                {{ label }}
            </div>
            <div class="list-box-item list-box-more">+ {{ labels.length }}</div>
        </div>
    </div>
</template>

<script>
const escapeHtml = (text) =>
    String(text).replace(
        /[&<>"']/g,
        (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[character]
    );

const tooltipTemplate = (classes) =>
    `<div class="tooltip ${classes}" role="tooltip"><div class="arrow"></div><div class="tooltip-inner"></div></div>`;

// A --tooltip-* timing from master.scss (":root"), in ms.
const tooltipTiming = (name, fallback) => {
    const value = window.getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    const number = parseFloat(value);
    if (Number.isNaN(number)) {
        return fallback;
    }

    return value.endsWith("ms") || !value.endsWith("s") ? number : number * 1000;
};

// Shared by every list box on the page: moving from one tooltip to the next skips the show delay.
const tooltipState = { open: null, lastHiddenAt: 0 };

export default {
    props: {
        items: {
            type: Object,
            default: () => ({}),
        },
        inputStyle: {
            type: String,
            default: "",
        },
        // How many items to show before collapsing the rest into "+N". One by default, so
        // the datatable cells and select popups already using this component are unchanged.
        visibleLimit: {
            type: Number,
            default: 1,
        },
        // Opt-in: instead of visibleLimit, show as many items as fit in this many px (at least one),
        // then "+N".
        fitWidth: {
            type: Number,
            default: null,
        },
    },

    data() {
        return {
            fitCount: null,
            resizeTimer: null,
        };
    },

    created() {
        // per tooltip element: its pending show/hide or auto-dismiss timer (not reactive)
        this.tooltipTimers = new Map();
    },

    computed: {
        isFitWidth() {
            return this.fitWidth !== null;
        },

        // The item keys are the labels; the values are unused.
        labels() {
            return Object.keys(this.items ?? {});
        },

        visibleCount() {
            if (!this.isFitWidth) {
                return this.visibleLimit;
            }

            return this.fitCount ?? this.labels.length;
        },

        visibleLabels() {
            return this.labels.slice(0, this.visibleCount);
        },

        collapsedLabels() {
            return this.labels.slice(this.visibleCount);
        },

        collapsedItems() {
            return this.collapsedLabels.join(", ");
        },

        // fitWidth: the hidden values as separate tags on a light card, instead of one comma-joined line.
        // Bootstrap reads the tooltip from data-original-title, so binding it (rather than title) keeps it
        // current when the labels change and avoids the browser's own tooltip showing the markup.
        moreTooltipAttributes() {
            if (!this.isFitWidth) {
                return { title: this.collapsedItems };
            }

            return {
                "data-trigger": "manual",
                "data-html": "true",
                "data-template": tooltipTemplate("list-box-tooltip list-box-more-tooltip"),
                "data-original-title": this.collapsedLabels
                    .map((label) => `<span class="list-box-tooltip-item">${escapeHtml(label)}</span>`)
                    .join(""),
            };
        },

        // fitWidth: the tooltips are shown and hidden here with the --tooltip-* timings, not on Bootstrap's hover
        tooltipListeners() {
            return this.isFitWidth ? { mouseenter: this.showTooltipSoon, mouseleave: this.hideTooltipSoon } : {};
        },
    },

    watch: {
        labels() {
            this.$nextTick(this.measure);
        },
        fitWidth() {
            this.$nextTick(this.measure);
        },
    },

    mounted() {
        if (!this.isFitWidth) {
            return;
        }
        this.measure();
        // a tag's own max-width follows the viewport, so its measured width can change on resize
        window.addEventListener("resize", this.onResize);
    },

    beforeDestroy() {
        window.removeEventListener("resize", this.onResize);
        clearTimeout(this.resizeTimer);
        this.tooltipTimers.forEach((timer, element) => {
            clearTimeout(timer);
            this.toggleTooltip(element, "hide");
        });
    },

    methods: {
        labelTooltipAttributes(label) {
            if (!this.isFitWidth) {
                return { title: label };
            }

            return {
                "data-trigger": "manual",
                "data-template": tooltipTemplate("list-box-tooltip"),
                "data-original-title": label,
            };
        },

        showTooltipSoon(event) {
            const element = event.currentTarget;
            // A tag showing its whole name needs no tooltip; only one cut short with "…" shows its full name.
            const isMore = element.classList.contains("list-box-more");
            if (!isMore && element.scrollWidth <= element.clientWidth) {
                return;
            }

            const reshow =
                (tooltipState.open && tooltipState.open.isConnected) ||
                Date.now() - tooltipState.lastHiddenAt <= tooltipTiming("--tooltip-reshow-window", 800);

            this.queueTooltip(element, "show", reshow ? 0 : tooltipTiming("--tooltip-show-delay", 300));
        },

        hideTooltipSoon(event) {
            this.queueTooltip(event.currentTarget, "hide", tooltipTiming("--tooltip-hide-delay", 100));
        },

        queueTooltip(element, action, delay) {
            clearTimeout(this.tooltipTimers.get(element));
            this.tooltipTimers.set(
                element,
                setTimeout(() => this.toggleTooltip(element, action), delay)
            );
        },

        toggleTooltip(element, action) {
            this.tooltipTimers.delete(element);
            if (typeof $ === "undefined" || typeof $.fn?.tooltip !== "function") {
                return;
            }

            if (action === "show") {
                if (!element.isConnected) {
                    return;
                }
                $(element).tooltip("show");
                tooltipState.open = element;

                const autoDismiss = tooltipTiming("--tooltip-auto-dismiss", 0);
                if (autoDismiss > 0) {
                    this.queueTooltip(element, "hide", autoDismiss);
                }
                return;
            }

            $(element).tooltip("hide");
            if (tooltipState.open === element) {
                tooltipState.open = null;
                tooltipState.lastHiddenAt = Date.now();
            }
        },

        onResize() {
            clearTimeout(this.resizeTimer);
            this.resizeTimer = setTimeout(this.measure, 150);
        },

        measure() {
            if (!this.isFitWidth || !this.$refs.measure) {
                return;
            }

            const elements = Array.from(this.$refs.measure.children);
            const more = elements.pop();
            const widths = elements.map((element) => element.getBoundingClientRect().width);

            const style = window.getComputedStyle(this.$el);
            const gap = parseFloat(style.columnGap) || 0;
            const available =
                this.fitWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0);

            const rowWidth = (count) =>
                widths.slice(0, count).reduce((sum, width, index) => sum + width + (index > 0 ? gap : 0), 0);

            if (rowWidth(widths.length) <= available) {
                this.fitCount = widths.length;
                return;
            }

            // Leave room for the "+N" badge after the last tag shown.
            const moreWidth = more.getBoundingClientRect().width + gap;
            let count = 0;
            while (count < widths.length && rowWidth(count + 1) + moreWidth <= available) {
                count++;
            }

            this.fitCount = Math.max(1, count);
        },
    },
};
</script>
